import { Box, Flex, ScrollArea, Tooltip, ActionIcon } from '@mantine/core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkflowChrome } from '../../../../../../components/Layout/WorkflowChromeContext';
import { toast } from 'react-hot-toast';
import {
  useCreateFormPFJurisdiction,
  useGetFormPFJurisdiction,
  useUpdateFormPFJurisdiction
} from '../../../../../../services/jurisdiction.service';
import agentStore from '../../../../../../store/agent';
import { WEB_PAGE_STATE_ENUMS } from '../../../../../../components/ChatWidget/constants';
import { FormPFFundInformationTable } from '.././components/FormPFFundInformationTable';
import { FormPFManagerInfo } from '.././components/FormPFManagerInfo';
import ErrorBoundary from '../../../../../../components/ErrorBoundary';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { BsArrowLeft, BsArrowRight } from 'react-icons/bs';
import { ManagerPanel, FundsSectionWrapper, ModalTitle } from './style';
import { extractApiError } from '../../../../../../utils';

const DEFAULT_FUND_SECTIONS = ['1b'];

const getDefaultFormState = () => ({
  sec_number: '',
  nfa_id: '',
  lei_id: '',
  large_trader_id: '',
  large_trader_id_suffix: '',
  fiscal_year_end: null,
  manager_sections: ['1a'],
  funds: []
});

const normalizeFormFundsFromAPI = (funds, jurisdictionFunds) =>
  funds?.map(fund => {
    const jurisdictionFund = jurisdictionFunds?.find(jf => jf?.fund_id === fund?.id) || {};
    // auto included by default
    let isIncludedFund = true;

    if (typeof jurisdictionFund?.is_included === 'boolean') {
      isIncludedFund = jurisdictionFund?.is_included;
    } else if (typeof fund?.is_included === 'boolean') {
      isIncludedFund = fund?.is_included;
    }

    // Extract required_sections from the sections array returned by the API,
    // and always include the mandatory default fund section.
    const required_sections = [
      ...new Set([
        ...DEFAULT_FUND_SECTIONS,
        ...(jurisdictionFund?.sections?.map(s => s?.section_key).filter(Boolean) || [])
      ])
    ];

    // merge fund info with the data coming from the API
    return {
      fund_id: fund?.id || jurisdictionFund?.fund_id,
      ...fund,
      ...jurisdictionFund,
      is_included: isIncludedFund,
      required_sections
    };
  });

const formatFiscalYearEndDate = value => {
  try {
    let format = value;

    if (value instanceof Date) {
      const y = value.getFullYear();
      const m = String(value.getMonth() + 1).padStart(2, '0');
      const d = String(value.getDate()).padStart(2, '0');
      format = `${y}-${m}-${d}`;
    }

    return format;
  } catch (_) {
    return null;
  }
};

// Used just like Jurisdiction modal, but for Form PF - (USA regulations)
export const FormPFFundInformation = ({
  open,
  funds,
  onClose,
  periodId,
  reportType,
  projectName,
  workflowId
}) => {
  const { data: jurisdiction, refetch: refetchJurisdiction } = useGetFormPFJurisdiction({
    periodId,
    queryParams: {
      enabled: Boolean(periodId)
    }
  });
  const createFormPFJurisdictionMutation = useCreateFormPFJurisdiction();
  const updateFormPFJurisdictionMutation = useUpdateFormPFJurisdiction();
  const navigate = useNavigate();
  const isCreatingOrIsUpdating =
    createFormPFJurisdictionMutation.isLoading || updateFormPFJurisdictionMutation.isLoading;

  const jurisdictionFunds = useMemo(() => {
    if (Array.isArray(jurisdiction?.funds) && jurisdiction.funds?.length) {
      return jurisdiction.funds.map(fund => ({
        id: fund?.fund_id,
        ...fund
      }));
    }
    return [];
  }, [jurisdiction]);

  const [form, setForm] = useState(getDefaultFormState());
  const [hasChanges, setHasChanges] = useState(false);
  const [forceExpand, setForceExpand] = useState();
  const [managerCollapsed, setManagerCollapsed] = useState(false);

  const handleManagerInputChange = useCallback(
    (key, value) => {
      setHasChanges(true);
      setForm(prev => {
        if (key === 'manager_sections') {
          // When 2a is deselected, auto-remove 2b from all funds
          const newFunds = value.includes('2a')
            ? prev.funds
            : prev.funds.map(f => ({
                ...f,
                required_sections: (f.required_sections || []).filter(s => s !== '2b')
              }));
          return { ...prev, manager_sections: value, funds: newFunds };
        }
        return { ...prev, [key]: value };
      });
    },
    [setForm, setHasChanges]
  );

  const handleManagerFiscalYearChange = value => {
    setHasChanges(true);
    setForm(prev => ({
      ...prev,
      fiscal_year_end: value
    }));
  };

  const handleIsFundIncludedChange = useCallback(
    (isChecked, fundId) => {
      setForm(prev => ({
        ...prev,
        funds: prev.funds.map(fund =>
          fund.id === fundId ? { ...fund, is_included: isChecked } : fund
        )
      }));
    },
    [setForm]
  );

  const handleFundBaseCurrencyChange = (currency, fundId) => {
    setForm(prev => ({
      ...prev,
      funds: prev.funds.map(fund =>
        fund.id === fundId ? { ...fund, base_currency: currency } : fund
      )
    }));
  };

  const handleFundInputChange = (inputKey, inputValue, fundId) => {
    setForm(prev => ({
      ...prev,
      funds: prev.funds.map(fund =>
        fund.id === fundId ? { ...fund, [inputKey]: inputValue } : fund
      )
    }));
  };

  const handleFundTypeChange = (types, fundId) => {
    setHasChanges(true);
    setForm(prev => {
      const fund = prev.funds.find(f => f.id === fundId);
      const extractType = ft => (typeof ft === 'string' ? ft : ft?.fund_type);
      const prevTypeStrings = (fund?.fund_types || []).map(extractType);
      const newTypeStrings = types.map(extractType);

      const hadHedge = prevTypeStrings.includes('Hedge Fund');
      const hasHedge = newTypeStrings.includes('Hedge Fund');

      let newSections = fund?.required_sections || DEFAULT_FUND_SECTIONS;
      if (!hadHedge && hasHedge && !newSections.includes('1c')) {
        newSections = [...newSections, '1c'];
      } else if (hadHedge && !hasHedge) {
        newSections = newSections.filter(s => s !== '1c');
      }

      return {
        ...prev,
        funds: prev.funds.map(f =>
          f.id === fundId ? { ...f, fund_types: types, required_sections: newSections } : f
        )
      };
    });
  };

  const handleFundSectionsChange = (sections, fundId) => {
    setForm(prev => {
      const fund = prev.funds.find(f => f.id === fundId);
      const prevSections = fund?.required_sections || DEFAULT_FUND_SECTIONS;

      let newFundTypes = fund?.fund_types || [];
      const hadC1 = prevSections.includes('1c');
      const hasC1 = sections.includes('1c');

      // If 1c was added → also add Hedge Fund to types
      if (!hadC1 && hasC1) {
        const alreadyHasHedge = newFundTypes.some(
          ft => (typeof ft === 'string' ? ft : ft?.fund_type) === 'Hedge Fund'
        );
        if (!alreadyHasHedge) {
          newFundTypes = [...newFundTypes, { fund_type: 'Hedge Fund' }];
        }
      }

      // If 1c was removed → also remove Hedge Fund from types
      if (hadC1 && !hasC1) {
        newFundTypes = newFundTypes.filter(ft => {
          const typeStr = typeof ft === 'string' ? ft : ft?.fund_type;
          return typeStr !== 'Hedge Fund';
        });
      }

      return {
        ...prev,
        funds: prev.funds.map(f =>
          f.id === fundId ? { ...f, required_sections: sections, fund_types: newFundTypes } : f
        )
      };
    });
  };

  const handleDeleteFund = useCallback(
    fundId => {
      setHasChanges(true);
      setForm(prev => ({
        ...prev,
        funds: prev.funds.filter(f => f.id !== fundId)
      }));
    },
    [setForm, setHasChanges]
  );

  const handleFormPfSubmit = useCallback(
    e => {
      e.preventDefault();

      if (!form?.fiscal_year_end) {
        toast.error('Fiscal Year End is required');
        return;
      }
      const isMissingFundType = form?.funds?.some(f => !f?.fund_types?.length);

      if (isMissingFundType) {
        toast.error('Fund Type is required');
        return;
      }

      const parsedFiscalYear = formatFiscalYearEndDate(form.fiscal_year_end);

      // After save success, advance the user to Source Files (Upload Files)
      // — the next stepper stage after Fund Info. Calling the parent's
      // `onClose` used to route through Quarters/Jurisdictions/handleClose,
      // which bounced FormPF periods to /add-aifs because they're stored
      // as template_type === 'sratch'. Navigating directly here decouples
      // FormPF's "Save and next" UX from the scratch-branch in the
      // parent's handler.
      const goNext = () => navigate(`/project/quaters/${workflowId}/${periodId}/upload-files`);

      if (jurisdiction?.id) {
        updateFormPFJurisdictionMutation.mutate(
          {
            periodId,
            workflowId,
            data: { ...form, fiscal_year_end: parsedFiscalYear }
          },
          {
            onSuccess: () => {
              refetchJurisdiction();
              toast.success('Successfully updated Fund Information');
              setHasChanges(false);
              goNext();
            },
            onError: err => {
              toast.error(extractApiError(err, 'Failed to update Fund Information'));
            }
          }
        );
      } else {
        createFormPFJurisdictionMutation.mutate(
          {
            periodId,
            workflowId,
            data: { ...form, fiscal_year_end: parsedFiscalYear }
          },
          {
            onSuccess: () => {
              refetchJurisdiction();
              toast.success('Successfully created Fund Information');
              setHasChanges(false);
              goNext();
            },
            onError: err => {
              toast.error(extractApiError(err, 'Failed to create Fund Information'));
            }
          }
        );
      }
    },
    [form, jurisdiction, navigate, workflowId, periodId]
  );

  useEffect(() => {
    agentStore.setCurrentSubScreen(WEB_PAGE_STATE_ENUMS.JurisdictionsDrawer);
    return () => agentStore.setCurrentSubScreen('');
  }, []);

  useEffect(() => {
    const formFunds = normalizeFormFundsFromAPI(funds, jurisdictionFunds);
    const rawSections = jurisdiction?.manager_sections;
    const manager_sections =
      Array.isArray(rawSections) && rawSections.length
        ? rawSections.includes('1a')
          ? rawSections
          : ['1a', ...rawSections]
        : ['1a'];
    setForm(prev => ({ ...prev, ...(jurisdiction || {}), manager_sections, funds: formFunds }));
  }, [jurisdiction, jurisdictionFunds, funds]);

  const { setToolbarSlots, setLabels } = useWorkflowChrome();

  // Ref so the toolbar slot effect doesn't refire on every render — the
  // submit closure changes identity each tick via state.
  const handleFormPfSubmitRef = useRef();
  useEffect(() => {
    handleFormPfSubmitRef.current = handleFormPfSubmit;
  });

  // Only Save lives in the page chrome's toolbar. Cancel was removed —
  // the stepper's back arrow already covers "leave this step" navigation,
  // and the in-page top-bar (project / period chips) carries the breadcrumb
  // out, so a separate Cancel button was redundant.
  useEffect(() => {
    setToolbarSlots({
      primaryAction: {
        label: `Save (${form?.funds?.length ?? 0})`,
        onClick: () => handleFormPfSubmitRef.current?.({ preventDefault: () => {} }),
        disabled: !!isCreatingOrIsUpdating,
        loading: !!isCreatingOrIsUpdating,
        variant: 'primary',
        size: 'xs'
      }
    });
    return () => setToolbarSlots(null);
  }, [form?.funds?.length, isCreatingOrIsUpdating, setToolbarSlots]);

  // SEC-ID uniqueness reminder lives in the toolbar's left slot. Compact
  // inline form — no border, small icon, tight icon-text spacing.
  useEffect(() => {
    setLabels({
      toolbarLeftContent: (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            color: '#1279e8',
            fontSize: 12,
            lineHeight: 1.2
          }}
        >
          <AiOutlineInfoCircle size={13} />
          Manager and Fund SEC IDs must be unique
        </span>
      )
    });
    return () => setLabels({ toolbarLeftContent: null });
  }, [setLabels]);

  return (
    <Box
      p="16px"
      h="100%"
      bg="gray.0"
      style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
    >
      <Box
        bg="white"
        p="16px"
        style={{
          borderRadius: 8,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0
        }}
      >
        <ModalTitle>
          <span>Fund Information</span>
        </ModalTitle>
        <ErrorBoundary componentName="FormPFFundInformation">
          <form
            onSubmit={handleFormPfSubmit}
            style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
          >
            <Flex align="flex-start" gap={16} style={{ flex: 1, minHeight: 0 }}>
              <ManagerPanel $collapsed={managerCollapsed}>
                <Flex justify="flex-end" mb={managerCollapsed ? 0 : 4}>
                  <Tooltip
                    label={managerCollapsed ? 'Expand manager panel' : 'Collapse manager panel'}
                    withArrow
                    position="right"
                  >
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="md"
                      onClick={() => setManagerCollapsed(v => !v)}
                      aria-label={
                        managerCollapsed ? 'Expand manager panel' : 'Collapse manager panel'
                      }
                    >
                      {managerCollapsed ? <BsArrowRight /> : <BsArrowLeft />}
                    </ActionIcon>
                  </Tooltip>
                </Flex>

                {!managerCollapsed && (
                  <ScrollArea.Autosize style={{ flex: 1, minHeight: 0 }} type="auto">
                    <FormPFManagerInfo
                      sec_number={form?.sec_number}
                      nfa_id={form?.nfa_id}
                      lei_id={form?.lei_id}
                      large_trader_id={form?.large_trader_id}
                      large_trader_id_suffix={form?.large_trader_id_suffix}
                      fiscal_year_end={form?.fiscal_year_end}
                      manager_sections={form?.manager_sections}
                      handleManagerInputChange={handleManagerInputChange}
                      handleManagerFiscalYearChange={handleManagerFiscalYearChange}
                      projectName={projectName}
                    />
                  </ScrollArea.Autosize>
                )}
              </ManagerPanel>

              <FundsSectionWrapper>
                <ScrollArea.Autosize style={{ flex: 1, minHeight: 0 }} type="auto">
                  <FormPFFundInformationTable
                    funds={form?.funds || []}
                    forceExpand={forceExpand}
                    handleIsFundIncludedChange={handleIsFundIncludedChange}
                    closeForceExpand={() => setForceExpand(false)}
                    handleFundBaseCurrencyChange={handleFundBaseCurrencyChange}
                    handleFundInputChange={handleFundInputChange}
                    handleFundTypeChange={handleFundTypeChange}
                    handleFundSectionsChange={handleFundSectionsChange}
                    handleDeleteFund={handleDeleteFund}
                    managerSections={form?.manager_sections}
                  />
                  <div id="jurisdiction-forms__end" />
                </ScrollArea.Autosize>
              </FundsSectionWrapper>
            </Flex>
          </form>
        </ErrorBoundary>
      </Box>
    </Box>
  );
};
